import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

import api from "@/common/utils/api";
import { orgQueryKeys } from "@/global-settings/data/orgQueryKeys";
import { ErrorResponse } from "@reflag/shared/api";
import {
  CreateOrganizationDomainType,
  OrganizationDomainDTO,
} from "@reflag/shared/organizationAPI";

export function useOrganizationDomains(orgId: string) {
  return useQuery({
    queryKey: orgQueryKeys.domains(orgId),
    queryFn: async () => {
      const res = await api.get<"/orgs/:orgId/domains">(
        `/orgs/${orgId}/domains`,
      );
      return res.data;
    },
  });
}

export function useCreateOrganizationDomainMutation(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    OrganizationDomainDTO,
    AxiosError<ErrorResponse>,
    CreateOrganizationDomainType
  >({
    mutationFn: async (data) => {
      const res = await api.post<"/orgs/:orgId/domains">(
        `/orgs/${orgId}/domains`,
        data,
      );
      return res.data.domain;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgQueryKeys.domains(orgId) });
    },
  });
}

export function useDeleteOrganizationDomainMutation(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation<object, AxiosError<ErrorResponse>, { domainId: string }>({
    mutationFn: async ({ domainId }) => {
      const res = await api.delete<"/orgs/:orgId/domains/:domainId">(
        `/orgs/${orgId}/domains/${domainId}`,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgQueryKeys.domains(orgId) });
    },
  });
}
